/**
 * PixelOffice 精灵配置（被 index.tsx 和 useOfficeStatus.ts 使用）
 *
 * 背景与气泡文本保留在此；具体精灵布局已内联到 PixelOfficeCanvas.tsx，
 * 与 index.html 的 Phaser preload/create 实现保持一致。
 */

// ─── 背景图（换背景功能使用） ──────────────────────────────────────────────────

// 六套主题背景来自 index.html 的 themeMap：
//   forest=sl, modern=xd, snow=xt, desert=sm, palace=gt, garden=ty
import imgBgSl from '@src/assets/pixel-office/sl.webp'; // 森林（index.html 默认）
import imgBgXd from '@src/assets/pixel-office/xd.webp'; // 现代
import imgBgXt from '@src/assets/pixel-office/xt.webp'; // 雪天
import imgBgSm from '@src/assets/pixel-office/sm.webp'; // 沙漠
import imgBgGt from '@src/assets/pixel-office/gt.webp'; // 宫廷
import imgBgTy from '@src/assets/pixel-office/ty.webp'; // 庭园

/** 可用背景图列表，随机换背景时从此取样（首项为默认） */
export const BG_IMAGES: string[] = [
  imgBgSl,          // 森林（默认，与 index.html 一致）
  imgBgXd,          // 现代
  imgBgXt,          // 雪天
  imgBgSm,          // 沙漠
  imgBgGt,          // 宫廷
  imgBgTy,          // 庭园
];

// ─── 状态类型 ──────────────────────────────────────────────────────────────────

export type OfficeStatus = 'idle' | 'writing' | 'researching' | 'executing' | 'syncing' | 'error';

export const OFFICE_STATUS_LABELS: Record<OfficeStatus, string> = {
  idle: '待命中',
  writing: '整理文档',
  researching: '搜索信息',
  executing: '执行任务',
  syncing: '同步备份',
  error: '出错了',
};

// ─── 状态气泡文本（来自 index.html BUBBLE_TEXTS.zh） ─────────────────────────

export const BUBBLE_TEXTS: Record<OfficeStatus, string[]> = {
  idle: [
    '待命中：耳朵竖起来了',
    '我在这儿，随时可以开工',
    '先把桌面收拾干净再说',
    '呼——给大脑放个风',
    '今天也要优雅地高效',
    '等待，是为了更准确的一击',
    '咖啡还热，灵感也还在',
    '状态：静心 / 充电',
    '小猫说：慢一点也没关系',
  ],
  writing: [
    '进入专注模式：勿扰',
    '先把关键路径跑通',
    '我来把复杂变简单',
    '把 bug 关进笼子里',
    '写到一半，先保存',
    '把每一步都做成可回滚',
    '今天的进度，明天的底气',
    '稳住，我们能赢',
  ],
  researching: [
    '我在挖证据链',
    '让我把信息熬成结论',
    '找到了：关键在这里',
    '先把变量控制住',
    '我在查：它为什么会这样',
    '先定位，再优化',
    '别急，先画因果图',
  ],
  executing: [
    '执行中：不要眨眼',
    '把任务切成小块逐个击破',
    '开始跑 pipeline',
    '一键推进：走你',
    '让结果自己说话',
    '先做最小可行，再做最美版本',
  ],
  syncing: [
    '同步中：把今天锁进云里',
    '备份不是仪式，是安全感',
    '写入中…别断电',
    '把变更交给时间戳',
    '云端对齐：咔哒',
    '同步完成前先别乱动',
    '多一份备份，少一份后悔',
  ],
  error: [
    '警报响了：先别慌',
    '我闻到 bug 的味道了',
    '先复现，再谈修复',
    '把日志给我，我会说人话',
    '错误不是敌人，是线索',
    '先止血，再手术',
    '我在：马上定位根因',
    '别怕，这种我见多了',
  ],
};

/** 猫咪气泡文本（来自 index.html BUBBLE_TEXTS.zh.cat） */
export const CAT_BUBBLE_TEXTS: string[] = [
  '喵~',
  '咕噜咕噜…',
  '尾巴摇一摇',
  '晒太阳最开心',
  '有人来看我啦',
  '我是这个办公室的吉祥物',
  '伸个懒腰',
  '今天的罐罐准备好了吗',
  '呼噜呼噜',
  '这个位置视野最好',
];

// ─── DrawableSprite（保留 PixelOfficeCanvas 旧版兼容类型，未使用） ───────────
// PixelOfficeCanvas.tsx 已不再依赖此接口（改用内部 SpriteConf），
// 此处仅保留，避免 useOfficeStatus.ts 之外的文件引用报错。

export interface DrawableSprite {
  key: string;
  src: string;
  frameW: number;
  frameH: number;
  frameStart: number;
  frameEnd: number;
  staticFrame: number;
  fps: number;
  x: number;
  y: number;
  scale: number;
  depth: number;
  defaultVisible: boolean;
  interactive?: boolean;
  pingPong?: { leftX: number; rightX: number; speed: number };
}

export const SPRITES: DrawableSprite[] = [];

export const STATUS_VISIBILITY: Record<
  OfficeStatus,
  { show: string[]; hide: string[]; serverroomAnimate: boolean }
> = {
  idle:       { show: ['star_idle'], hide: ['star_working', 'sync_anim', 'error_bug'], serverroomAnimate: false },
  writing:    { show: ['star_working'], hide: ['star_idle', 'sync_anim', 'error_bug'], serverroomAnimate: true },
  researching:{ show: ['star_working'], hide: ['star_idle', 'sync_anim', 'error_bug'], serverroomAnimate: true },
  executing:  { show: ['star_working'], hide: ['star_idle', 'sync_anim', 'error_bug'], serverroomAnimate: true },
  syncing:    { show: ['sync_anim'], hide: ['star_idle', 'star_working', 'error_bug'], serverroomAnimate: true },
  error:      { show: ['error_bug'], hide: ['star_idle', 'star_working', 'sync_anim'], serverroomAnimate: false },
};
